import React from 'react';
import { ChevronDown, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import { colors } from '../styles/colors';
import { formatEuro } from '../utils/formatters';

/**
 * Composant d'affichage d'un tableau de charges par type
 */
function ChargesTable({
  type,
  charges,
  groupedCharges,
  nombreConsultants,
  consultantsMap = {},
  expandedRows,
  onToggleRow,
  onEditGroup,
  onDeleteGroup,
  onEditCharge,
  onDeleteCharge,
  getMoisLabel,
  canEdit = true
}) {
  const typeTotal = charges.reduce((sum, charge) => sum + charge.montant, 0);

  // Masquer le tableau s'il est vide
  if (charges.length === 0) {
    return null;
  }

  return (
    <div style={{
      marginBottom: '2rem',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Sous-total par type */}
      <div style={{
        backgroundColor: colors.text.white,
        padding: '1rem 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '0.5rem'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: '1.25rem',
          fontWeight: '600',
          color: colors.primary.teal,
          fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
        }}>
          Charges {type}s ({charges.length})
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingRight: '1.5rem' }}>
          <span style={{
            fontSize: '0.875rem',
            color: colors.primary.gray,
            fontFamily: '"Noto Sans", sans-serif'
          }}>
            Total :
          </span>
          <span style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: colors.primary.gold,
            fontFamily: '"Noto Sans", sans-serif'
          }}>
            {formatEuro(typeTotal)}
          </span>
        </div>
      </div>

      {/* Tableau */}
      <div style={{
        backgroundColor: 'white',
        border: `1.5px solid ${colors.primary.gray}`,
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: colors.primary.gray }}>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                width: '40px'
              }}>
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                Nom
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                width: '130px'
              }}>
                Consultant
              </th>
              {type !== 'Annuelle' && (
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  width: '100px'
                }}>
                  {type === 'Exceptionnelle' ? 'Mois' : 'Occurrences'}
                </th>
              )}
              <th style={{
                padding: '0.75rem',
                textAlign: 'right',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                width: '150px'
              }}>
                Montant Total
              </th>
              {nombreConsultants > 0 && (
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'right',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  width: '150px'
                }}>
                  Quote-part
                </th>
              )}
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                width: '120px'
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedCharges.map((group, groupIndex) => {
              const rowKey = `${type}-${group.nom}`;
              const isExpanded = expandedRows[rowKey];
              const hasMultiple = group.charges.length > 1;

              return (
                <React.Fragment key={rowKey}>
                  {/* Ligne principale regroupée */}
                  <tr
                    style={{
                      borderBottom: `1px solid ${colors.secondary.lightGray}`,
                      backgroundColor: groupIndex % 2 === 0 ? 'white' : colors.secondary.lightGray
                    }}
                  >
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {hasMultiple && type !== 'Annuelle' && (
                        <button
                          onClick={() => onToggleRow(rowKey)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: colors.primary.teal,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', color: colors.primary.gray, fontWeight: '600' }}>
                      {group.nom}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', color: colors.primary.gray, fontSize: '0.8125rem' }}>
                      {group.charges[0].consultant
                        ? (consultantsMap[group.charges[0].consultant] || '—')
                        : <span style={{ color: colors.secondary.mediumGray }}>Tous</span>
                      }
                    </td>
                    {type !== 'Annuelle' && (
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: colors.primary.gray }}>
                        {type === 'Exceptionnelle' ? getMoisLabel(group.charges[0].mois) : group.charges.length}
                      </td>
                    )}
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: colors.primary.teal }}>
                      {formatEuro(group.totalMontant)}
                    </td>
                    {nombreConsultants > 0 && (
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: colors.primary.gold }}>
                        {group.charges[0].consultant
                          ? formatEuro(group.totalMontant)
                          : formatEuro(group.totalMontant / nombreConsultants)
                        }
                      </td>
                    )}
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {hasMultiple ? (
                          <>
                            <button
                              onClick={() => canEdit && onEditGroup(group)}
                              disabled={!canEdit}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: colors.primary.teal,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: !canEdit ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                opacity: !canEdit ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                              onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                              title={!canEdit ? "Vous n'avez pas les permissions pour éditer" : "Éditer toutes les charges du groupe"}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => canEdit && onDeleteGroup(group)}
                              disabled={!canEdit}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: !canEdit ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                opacity: !canEdit ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                              onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                              title={!canEdit ? "Vous n'avez pas les permissions pour supprimer" : "Supprimer toutes les charges du groupe"}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => canEdit && onEditCharge(group.charges[0])}
                              disabled={!canEdit}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: colors.primary.teal,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: !canEdit ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                opacity: !canEdit ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                              onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                              title={!canEdit ? "Vous n'avez pas les permissions pour éditer" : "Éditer la charge"}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => canEdit && onDeleteCharge(group.charges[0])}
                              disabled={!canEdit}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: !canEdit ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                opacity: !canEdit ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                              onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                              title={!canEdit ? "Vous n'avez pas les permissions pour supprimer" : "Supprimer la charge"}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Lignes de détail (dépliées) */}
                  {hasMultiple && isExpanded && group.charges.map((charge, chargeIndex) => (
                    <tr
                      key={charge.id}
                      style={{
                        borderBottom: chargeIndex === group.charges.length - 1
                          ? `1px solid ${colors.secondary.lightGray}`
                          : `1px solid ${colors.secondary.lightTeal}`,
                        backgroundColor: colors.secondary.lightGray
                      }}
                    >
                      <td></td>
                      <td style={{ padding: '0.5rem 0.75rem 0.5rem 2rem', color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>
                        {type === 'Trimestrielle' ? charge.trimestre : getMoisLabel(charge.mois)}
                      </td>
                      <td></td>
                      {type !== 'Annuelle' && <td></td>}
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: colors.primary.gray }}>
                        {formatEuro(charge.montant)}
                      </td>
                      {nombreConsultants > 0 && (
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>
                          {charge.consultant
                            ? formatEuro(charge.montant)
                            : formatEuro(charge.montant / nombreConsultants)
                          }
                        </td>
                      )}
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => canEdit && onEditCharge(charge)}
                            disabled={!canEdit}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: colors.primary.teal,
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: !canEdit ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              opacity: !canEdit ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                            onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                            title={!canEdit ? "Vous n'avez pas les permissions pour éditer" : "Éditer la charge"}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => canEdit && onDeleteCharge(charge)}
                            disabled={!canEdit}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: !canEdit ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              opacity: !canEdit ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => canEdit && (e.currentTarget.style.opacity = '0.8')}
                            onMouseLeave={(e) => canEdit && (e.currentTarget.style.opacity = '1')}
                            title={!canEdit ? "Vous n'avez pas les permissions pour supprimer" : "Supprimer la charge"}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ChargesTable;
